import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiFetch } from '../lib/api'

export default function RequestDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [request, setRequest] = useState(null)
  const [patient, setPatient] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [showWarrantyModal, setShowWarrantyModal] = useState(false)
  const [warrantyReason, setWarrantyReason] = useState('')
  const [isSubmittingWarranty, setIsSubmittingWarranty] = useState(false)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [duplicateConfirmText, setDuplicateConfirmText] = useState('')

  const deliveredDateValue = request?.delivered_at || request?.deliveredAt || request?.delivered_date || request?.deliveredDate

  const canStopPolling = request?.status === 'delivered' || request?.status === 'cancelled'

  useEffect(() => {
    const fetchPatient = async (patientId) => {
      try {
        const res = await apiFetch(`/api/patients/${patientId}`)
        if (res.ok) {
          const patientData = await res.json()
          setPatient(patientData)
          return
        }
      } catch (e) {
        // Ignorar error de paciente para no bloquear
      }
      setPatient(null)
    }

    const load = async () => {
      try {
        const res = await apiFetch(`/api/requests/${id}`)
        if (!res.ok) {
          const errorText = await res.text()
          console.error(`Error ${res.status}: ${errorText}`)
          setError(`Error al cargar la solicitud (código ${res.status}). Verifica que el ID sea correcto y que el backend esté activo.`)
          setLoading(false)
          return
        }
        const data = await res.json()
        console.log('Initial request data:', data)
        setRequest(data)
        setLoading(false)

        if (data.patient_id) {
          fetchPatient(data.patient_id)
        }
      } catch (e) {
        console.error('Error cargando solicitud', e)
        setError(`Error de conexión: ${e.message}. Verifica que el backend esté corriendo en http://localhost:4000`)
        setLoading(false)
      }
    }

    load()

    // Polling para actualizar estado cada 3 segundos
    // Pero dejamos de hacer polling si ya está entregado o cancelado
    if (canStopPolling) return

    const poll = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/requests/${id}`)
        if (res.ok) {
          const data = await res.json()
          console.log('Polling data:', data)
          setRequest(data)
        }
      } catch (e) {
        // Silent fail en polling
      }
    }, 3000)

    return () => clearInterval(poll)
  }, [id, canStopPolling])

  const handleCancelRequest = async () => {
    if (!cancelReason.trim()) {
      alert('Por favor proporciona un motivo de cancelación')
      return
    }
    try {
      const res = await apiFetch(`/api/requests/${id}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason })
      })
      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.error || 'Error al cancelar solicitud')
      }
      await res.json()
      // Redirigir según el rol del usuario
      if (currentUser?.role === 'clinic') {
        navigate('/clinic-dashboard')
      } else if (currentUser?.role === 'workshop' || currentUser?.role === 'production') {
        navigate('/production')
      } else {
        navigate('/admin')
      }
    } catch (e) {
      console.error('Error cancelando solicitud', e)
      alert(`Error: ${e.message}`)
    }
  }

  const handleDuplicateRequest = async () => {
    if (duplicateConfirmText !== 'duplicar') {
      alert('Debes escribir "duplicar" para confirmar')
      return
    }
    if (!request?.patient_id) return
    
    try {
      // Preparar datos de la solicitud duplicada
      const formData = new FormData()
      formData.append('patientId', request.patient_id)
      formData.append('doctorName', request.doctor_name || '')
      formData.append('footSide', request.foot_side || '')
      formData.append('shoeSize', request.shoe_size || '')
      formData.append('templateType', request.template_type || '')
      formData.append('observations', request.observations || '')
      formData.append('specs', JSON.stringify(request.specs || {}))
      
      // Copiar adjuntos
      if (Array.isArray(request.files) && request.files.length > 0) {
        for (let i = 0; i < request.files.length; i++) {
          const file = request.files[i]
          if (typeof file === 'object' && file instanceof File) {
            formData.append('files', file)
          }
        }
      }
      
      // Crear la nueva solicitud
      const res = await apiFetch(`/api/requests`, {
        method: 'POST',
        body: formData
      })
      
      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.error || 'Error al duplicar solicitud')
      }
      
      const newRequest = await res.json()
      alert('Solicitud duplicada exitosamente')
      // Navegar al perfil del paciente para ver todas sus solicitudes
      navigate(`/patient/${request.patient_id}`)
      
      setShowDuplicateModal(false)
      setDuplicateConfirmText('')
    } catch (err) {
      console.error('Error duplicando solicitud:', err)
      alert(`Error: ${err.message}`)
    }
  }

  const handleWarranty = () => {
    setShowWarrantyModal(true)
  }

  const handleWarrantySubmit = async () => {
    if (!warrantyReason.trim()) {
      alert('Por favor proporciona un motivo de garantía')
      return
    }
    
    setIsSubmittingWarranty(true)
    try {
      const res = await apiFetch(`/api/requests/${id}/warranty`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: warrantyReason.trim() })
      })
      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.error || 'Error al marcar como garantía')
      }
      const data = await res.json()
      
      // Actualizar estado local
      setRequest(prev => ({ ...prev, status: 'warranty', warranty_reason: warrantyReason, warranty_date: new Date().toISOString() }))
      setShowWarrantyModal(false)
      setWarrantyReason('')
      
      alert('Solicitud marcada como garantía exitosamente')
    } catch (err) {
      alert(err.message)
    } finally {
      setIsSubmittingWarranty(false)
    }
  }
  // Calcular si la garantía está activa (30 días desde entrega)
  const isWarrantyActive = () => {
    if (!deliveredDateValue) return false
    const deliveredDate = new Date(deliveredDateValue)
    const now = new Date()
    const daysSinceDelivery = Math.floor((now - deliveredDate) / (1000 * 60 * 60 * 24))
    return daysSinceDelivery <= 30
  }

  const specs = request?.specs || {}

  const renderSpec = (name, data) => {
    if (!data) return null
    
    // Casos especiales para elevacionAL con la nueva estructura: izqSelected, izqMm, derSelected, derMm
    if (name === 'elevacionAL' && typeof data === 'object') {
      const hasValues = (data.izqSelected && data.izqMm) || (data.derSelected && data.derMm)
      if (!hasValues) return null
      
      const label = 'Elevación A.L.'
      return (
        <div key={name} className="p-3 bg-gray-50 rounded border border-gray-200">
          <p className="font-medium text-sm text-gray-900 mb-2">{label}</p>
          <div className="text-xs space-y-1">
            {data.izqSelected && data.izqMm && <p className="text-gray-700">Izq: <span className="font-medium">{data.izqMm}mm</span></p>}
            {data.derSelected && data.derMm && <p className="text-gray-700">Der: <span className="font-medium">{data.derMm}mm</span></p>}
          </div>
        </div>
      )
    }
    
    // Detectar tipo de dato
    const isSimpleCheckbox = typeof data === 'object' && 
      (data.izq === true || data.izq === false || data.der === true || data.der === false) &&
      !data.tipo && !data.mm && !data.izq?.mm && !data.der?.mm
    
    // Detectar si es un objeto con valores (números, strings, o sub-objetos como {mm: '12'})
    const hasValues = typeof data === 'object' && (
      (data.izq !== null && data.izq !== undefined && data.izq !== false && data.izq !== '') ||
      (data.der !== null && data.der !== undefined && data.der !== false && data.der !== '')
    )
    
    if (!hasValues && !isSimpleCheckbox) return null
    
    const label = name.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()
    
    // Simple checkbox (true/false values, no mm or tipo)
    if (isSimpleCheckbox) return (
      <div key={name} className="p-3 bg-gray-50 rounded border border-gray-200">
        <p className="font-medium text-sm text-gray-900 mb-2">{label}</p>
        <div className="flex gap-4 text-xs">
          {data.izq && <span className="text-red-600 font-medium">Izq ✓</span>}
          {data.der && <span className="text-red-600 font-medium">Der ✓</span>}
        </div>
      </div>
    )
    
    // Complex object with values
    if (hasValues) return (
      <div key={name} className="p-3 bg-gray-50 rounded border border-gray-200">
        <p className="font-medium text-sm text-gray-900 mb-2">{label}</p>
        <div className="text-xs space-y-1">
          {(data.izq !== null && data.izq !== undefined && data.izq !== false && data.izq !== '') && (
            <p className="text-gray-700">
              Izq: <span className="font-medium">
                {typeof data.izq === 'object' 
                  ? `${data.izq.tipo || ''} ${data.izq.mm || ''}mm`.trim() 
                  : String(data.izq)}
              </span>
            </p>
          )}
          {(data.der !== null && data.der !== undefined && data.der !== false && data.der !== '') && (
            <p className="text-gray-700">
              Der: <span className="font-medium">
                {typeof data.der === 'object' 
                  ? `${data.der.tipo || ''} ${data.der.mm || ''}mm`.trim() 
                  : String(data.der)}
              </span>
            </p>
          )}
        </div>
      </div>
    )
    
    return null
  }

  if (loading) return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <aside className="w-64 bg-gradient-to-b from-[#2c3e50] to-[#34495e] text-white flex flex-col shadow-2xl fixed h-screen">
        <div className="p-4 border-b border-white/10 flex justify-center">
          <img src="/lucvan-logo-web.png" alt="Logo" className="h-16 brightness-0 invert" />
        </div>
        <nav className="flex-1 px-3 py-6 space-y-2">
          <button
            onClick={() => navigate('/clinic')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all"
          >
            <img src="/logos/Pacientes.png" alt="Pacientes" className="w-5 h-5" />
            <span>Pacientes</span>
          </button>
          <button
            onClick={() => navigate('/clinic-dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all"
          >
            <img src="/logos/Solicitud.png" alt="Solicitudes" className="w-5 h-5" />
            <span>Solicitudes</span>
          </button>
        </nav>
      </aside>
      <main className="flex-1 overflow-auto ml-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p>Cargando...</p>
        </div>
      </main>
    </div>
  )
  if (error || !request) return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <aside className="w-64 bg-gradient-to-b from-[#2c3e50] to-[#34495e] text-white flex flex-col shadow-2xl fixed h-screen">
        <div className="p-4 border-b border-white/10 flex justify-center">
          <img src="/lucvan-logo-web.png" alt="Logo" className="h-16 brightness-0 invert" />
        </div>
      </aside>
      <main className="flex-1 overflow-auto ml-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">No se encontró la solicitud</h2>
          {error && <p className="text-gray-700 mb-4">{error}</p>}
          <button onClick={() => navigate(-1)} className="bg-[#0066A4] text-white px-4 py-2">← Volver</button>
        </div>
      </main>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-[#2c3e50] to-[#34495e] text-white flex flex-col shadow-2xl fixed h-screen">
        <div className="p-4 border-b border-white/10 flex justify-center">
          <img src="/lucvan-logo-web.png" alt="Logo" className="h-16 brightness-0 invert" />
        </div>
        <nav className="flex-1 px-3 py-6 space-y-2">
          <button
            onClick={() => navigate('/clinic')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all"
          >
            <img src="/logos/Pacientes.png" alt="Pacientes" className="w-5 h-5" />
            <span>Pacientes</span>
          </button>
          <button
            onClick={() => navigate('/clinic-dashboard')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all"
          >
            <img src="/logos/Solicitud.png" alt="Solicitudes" className="w-5 h-5" />
            <span>Solicitudes</span>
          </button>
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="mb-3">
            <p className="text-sm font-semibold truncate">{currentUser?.email}</p>
            <p className="text-xs text-gray-400 capitalize">{currentUser?.role === 'clinic' ? 'Clínica' : 'Administrador'}</p>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded font-medium transition-all"
          >
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto ml-64">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="self-start mb-6 px-3 py-2 text-[#0066A4] hover:bg-blue-50 rounded-lg transition-all flex items-center gap-2"
        >
          <span>←</span>
          <span className="text-sm font-medium">Atrás</span>
        </button>

        <div className="bg-white border border-gray-300 p-6">
          {/* Header con nombre del paciente y consecutivo */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 pb-6 border-b">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-[#003C63] mb-1">{patient?.name || request.patient_name || `Paciente #${request.patient_id}`}</h1>
              <p className="text-base text-gray-700 font-medium mb-2">{request.template_type || request.templateType || 'Solicitud'}</p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                <span className="font-semibold text-orange-600">Fecha de solicitud: <span className="font-normal text-gray-800">{new Date(request.created_at || request.createdAt).toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'})}</span></span>
              </div>
            </div>
            
            {/* Botones de acción */}
            <div className="flex flex-col items-end gap-3">
              <p className="text-xs text-gray-400">#{request.consecutive || request.id}</p>
              <div className="flex flex-wrap gap-2 justify-end">
              <button
                onClick={() => setShowDuplicateModal(true)}
                className="px-4 py-2 bg-yellow-400 text-[#003C63] rounded-md hover:bg-yellow-500 font-semibold transition-colors text-sm shadow-sm"
              >
                Duplicar
              </button>
              {request.status === 'delivered' && isWarrantyActive() && (
                <button
                  onClick={handleWarranty}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 font-medium transition-colors text-sm"
                  title="Activar garantía (válido por 30 días desde entrega)"
                >
                  Garantía
                </button>
              )}
              {request.status === 'delivered' && !isWarrantyActive() && (
                <button
                  disabled
                  className="px-4 py-2 bg-gray-400 text-white rounded-md cursor-not-allowed font-medium text-sm"
                  title="Garantía expirada (solo válida 30 días desde entrega)"
                >
                  Garantía (expirada)
                </button>
              )}
              {(currentUser?.role === 'admin' || (currentUser?.role === 'clinic' && request.status === 'pending')) && request.status !== 'cancelled' && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 font-medium transition-colors text-sm"
                >
                  {currentUser?.role === 'clinic' ? 'Solicitar Cancelación' : 'Cancelar Solicitud'}
                </button>
              )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 border-b pb-6 mb-6">
            <div>
              <p className="text-sm text-gray-600">Doctor</p>
              <p className="font-medium text-gray-900">{request.doctor_name || request.doctorName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Pie</p>
              <p className="font-medium text-gray-900">{request.foot_side || request.footSide}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Talla</p>
              <p className="font-medium text-gray-900">{request.shoe_size || request.shoeSize}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Estado</p>
              <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-semibold ${
                request.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                request.status === 'delivered' ? 'bg-green-100 text-green-800' :
                request.status === 'ready' ? 'bg-purple-100 text-purple-800' :
                request.status === 'in_production' || request.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {request.status === 'cancelled' ? 'Cancelada' : 
                 request.status === 'delivered' ? 'Entregada' : 
                 request.status === 'ready' ? 'Lista para Entregar' :
                 request.status === 'in_production' || request.status === 'in_progress' ? 'En Producción' : 'Pendiente'}
              </span>
              {request.status === 'delivered' && (
                <p className="text-sm text-gray-700 mt-2">
                  Fecha de entrega: <span className="font-semibold text-gray-900">{deliveredDateValue ? new Date(deliveredDateValue).toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'}) : 'No registrada'}</span>
                </p>
              )}
            </div>
          </div>

          {request.status === 'cancelled' && request.cancel_reason && (
            <div className="border-l-4 border-red-500 bg-red-50 p-4 mb-6">
              <h3 className="text-sm font-semibold text-red-900 mb-1">Motivo de Cancelación</h3>
              <p className="text-sm text-red-800">{request.cancel_reason}</p>
            </div>
          )}

          {request.observations && (
            <div className="border-b pb-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Observaciones</h2>
              <p className="text-gray-700">{request.observations}</p>
            </div>
          )}

          {Object.entries(specs).filter(([k, v]) => {
            // Solo mostrar specs que realmente tengan datos
            if (!v || typeof v !== 'object') return false
            
            // Caso especial para elevacionAL con nueva estructura
            if (k === 'elevacionAL') {
              return (v.izqSelected && v.izqMm) || (v.derSelected && v.derMm)
            }
            
            // Helper para verificar si un valor tiene contenido real
            const hasRealValue = (val) => {
              if (val === null || val === undefined || val === false || val === '') return false
              if (typeof val === 'object') {
                // Si es objeto, verificar que tenga al menos un campo con valor
                return Object.values(val).some(v => v !== null && v !== undefined && v !== '' && v !== false)
              }
              return true
            }
            
            // Casos generales - verificar que izq o der tengan valores reales
            return hasRealValue(v.izq) || hasRealValue(v.der)
          }).length > 0 && (
            <div className="border-b pb-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Especificaciones de Cuñas y Alzas</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Object.entries(specs).filter(([k, v]) => {
                  // Aplicar el mismo filtro para renderizar solo specs con datos
                  if (!v || typeof v !== 'object') return false
                  
                  // Caso especial para elevacionAL
                  if (k === 'elevacionAL') {
                    return (v.izqSelected && v.izqMm) || (v.derSelected && v.derMm)
                  }
                  
                  // Helper para verificar valores reales
                  const hasRealValue = (val) => {
                    if (val === null || val === undefined || val === false || val === '') return false
                    if (typeof val === 'object') {
                      return Object.values(val).some(v => v !== null && v !== undefined && v !== '' && v !== false)
                    }
                    return true
                  }
                  
                  // Casos generales
                  return hasRealValue(v.izq) || hasRealValue(v.der)
                }).map(([k, v]) => renderSpec(k, v))}
              </div>
            </div>
          )}

          {(Array.isArray(request.files) ? request.files.length > 0 : JSON.parse(request.files || '[]').length > 0) && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">📎 Adjuntos ({Array.isArray(request.files) ? request.files.length : JSON.parse(request.files || '[]').length})</h2>
              <div className="space-y-2">
                {(Array.isArray(request.files) ? request.files : JSON.parse(request.files || '[]')).map((file, idx) => {
                  const name = typeof file === 'string' ? file : (file.name || 'Archivo')
                  let url = typeof file === 'object' ? (file.url || file.path || '') : ''
                  
                  // Mejorar URL si es relativa: usar /uploads/<filename> como fallback
                  if (url && !url.startsWith('http')) {
                    if (url.startsWith('/api/uploads') || url.startsWith('/uploads/')) {
                      // ya es ruta relativa válida
                    } else {
                      const last = (url.split('/').pop() || '').trim()
                      if (last) {
                        url = `/uploads/${encodeURIComponent(last)}`
                      }
                    }
                  }
                  
                  const handleDownload = () => {
                    if (url) {
                      const link = document.createElement('a')
                      link.href = url
                      link.download = name || 'archivo'
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }
                  }
                  
                  return (
                    <div key={idx} className="flex items-center justify-between gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200">
                      <div className="flex items-center gap-3 min-w-0">
                        <svg className="w-5 h-5 text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                          {url && <p className="text-xs text-gray-500 truncate">{url}</p>}
                        </div>
                      </div>
                      <button
                        onClick={handleDownload}
                        disabled={!url}
                        className={`px-3 py-1.5 rounded text-xs font-semibold whitespace-nowrap flex-shrink-0 transition ${
                          url
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {url ? '⬇️ Descargar' : '⚠️ Sin URL'}
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
        </div>
      </main>

      {/* Modales */}
      {showWarrantyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4 text-orange-600">🔧 Marcar como Garantía</h2>

            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Solicitud #{request.consecutive || request.id}</strong>
                <br />
                Paciente: {patient?.name || request.patient_name || 'N/A'}
                <br />
                Esta solicitud será reabierta y enviada al taller para revisión.
              </p>
            </div>

            <p className="text-gray-600 mb-3 text-sm font-medium">
              Por favor describe el motivo de la garantía: <span className="text-red-600">*</span>
            </p>
            <textarea
              value={warrantyReason}
              onChange={(e) => setWarrantyReason(e.target.value)}
              placeholder="Ejemplo: Se despegó el material de la plantilla, ajuste no adecuado, defecto en el material..."
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500 mb-4"
              rows="4"
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={handleWarrantySubmit}
                disabled={!warrantyReason.trim() || isSubmittingWarranty}
                className="flex-1 bg-orange-600 text-white py-2.5 rounded-md font-medium hover:bg-orange-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmittingWarranty ? 'Enviando...' : 'Enviar a Garantía'}
              </button>
              <button
                onClick={() => {
                  setShowWarrantyModal(false)
                  setWarrantyReason('')
                }}
                disabled={isSubmittingWarranty}
                className="flex-1 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-md font-medium hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4 text-[#003C63]">⚠️ Cancelar y Eliminar Solicitud</h2>
            
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 font-semibold mb-2">¡ADVERTENCIA!</p>
              <p className="text-red-700 text-sm mb-2">
                Esta acción es <strong>irreversible</strong> y eliminará permanentemente:
              </p>
              <ul className="text-red-700 text-sm list-disc list-inside space-y-1">
                <li>La solicitud completa del sistema</li>
                <li>Todos los archivos adjuntos</li>
                <li>Toda la información asociada</li>
              </ul>
            </div>

            <p className="text-gray-600 mb-3 text-sm font-medium">
              Por favor proporciona el motivo de cancelación:
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ejemplo: Error en los datos del paciente, solicitud duplicada, ya no se requiere el servicio..."
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
              rows="4"
            />
            <div className="flex gap-3">
              <button
                onClick={handleCancelRequest}
                disabled={!cancelReason.trim()}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-md font-medium hover:bg-red-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Sí, Eliminar Solicitud
              </button>
              <button
                onClick={() => {
                  setShowCancelModal(false)
                  setCancelReason('')
                }}
                className="flex-1 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-md font-medium hover:bg-gray-50 transition"
              >
                No, Mantener
              </button>
            </div>
          </div>
        </div>
      )}

      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4 text-[#003C63]">📋 Duplicar Solicitud</h2>
            
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-yellow-800 text-sm">
                Se creará una nueva solicitud exactamente igual a la actual, con todos los archivos adjuntos.
              </p>
            </div>

            <p className="text-gray-600 mb-2 text-sm font-medium">
              Para confirmar, escribe la palabra <strong>"duplicar"</strong>:
            </p>
            <input
              type="text"
              value={duplicateConfirmText}
              onChange={(e) => setDuplicateConfirmText(e.target.value)}
              placeholder="duplicar"
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 mb-4"
            />
            
            <div className="flex gap-3">
              <button
                onClick={handleDuplicateRequest}
                disabled={duplicateConfirmText !== 'duplicar'}
                className="flex-1 bg-yellow-400 text-[#003C63] py-2.5 rounded-md font-semibold hover:bg-yellow-500 transition disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                Sí, Duplicar
              </button>
              <button
                onClick={() => {
                  setShowDuplicateModal(false)
                  setDuplicateConfirmText('')
                }}
                className="flex-1 bg-white border border-gray-300 text-gray-700 py-2.5 rounded-md font-medium hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
